import type DynamicForm from './DynamicForm';
import type DynamicFormControl from './DynamicFormControl';
import { IDictionary } from '@app/src/common/model/FormConfiguration';
import {
  IFormField,
  ISelectOption,
  PrefillType
} from '@app/src/common/model/FormField';
import { FieldType } from '@app/src/common/components/form/MobxFieldControl';
import {
  FormControlType,
  isDisplayOnlyControl
} from '@app/src/common/model/FormControls';
import { toList } from '@app/src/common/utils/listUtils';
import { resolveDictionaryKey } from './dictionaryKey';

/*
 * PrefillType.FORM_CONTROL: a field whose value follows another control of the form.
 *
 * Copy:      { "type": "formControl", "field": "contactId", "matchField": "email" }
 *            gets the source value, or the `matchField` attribute of its selected option(s).
 * Condition: { "type": "formControl", "field": "resources", "matchField": "tags",
 *              "matchValue": "biometry", "value": "<id>" }
 *            gets `value` when any of those values matches `matchValue`, its defaultValue otherwise.
 *
 * Kept free of stores so it can be unit tested.
 */

/**
 * The value a FORM_CONTROL prefill gives `field` for the source value, or null while it
 * cannot be told yet (the source dictionary has not loaded the selected options).
 */
export function resolveFormControlPrefill(
  field: IFormField,
  sourceValue: any,
  options?: ISelectOption[]
): { value: any } | null {
  const { matchField, matchValue, value } = field.prefill!;
  const selected = toList(sourceValue);
  let values = selected;
  if (matchField) {
    const found = selected.map(item =>
      options?.find(option => option.value === item)
    );
    if (found.some(option => !option)) {
      return null;
    }
    values = found.flatMap(option => toList(option!.option?.[matchField]));
  }
  if (matchValue === undefined) {
    // an info area shows every selected value whatever its type
    const isList =
      field.type === FieldType.ARRAY ||
      field.control === FormControlType.INFO_AREA;
    return { value: isList ? values : values[0] };
  }
  const expected = toList(matchValue).map(normalize);
  const isMatched = values.some(item => expected.includes(normalize(item)));
  return { value: isMatched ? value : field.defaultValue };
}

/**
 * Keeps FORM_CONTROL fields in step with their source on every change. The source may sit
 * in another form (wizard step). Call it after the other subscriptions of the forms:
 * onChanges keeps one callback, this one chains the handler already there.
 */
export function setupFormControlPrefill(
  forms: DynamicForm[],
  getDictionaries: () => IDictionary | undefined
) {
  findFormControlPrefills(forms).forEach(({ field, source, target }) => {
    const existingHandler = source._onChanges;
    source.onChanges.subscribe((value: any, oldValue: any) => {
      if (existingHandler) {
        existingHandler(value, oldValue);
      }
      if (value !== oldValue) {
        updateTarget(field, source, target, getDictionaries());
      }
    });
  });
}

/** Calculates FORM_CONTROL fields once from the current source values, e.g. on a new form. */
export function applyFormControlPrefill(
  forms: DynamicForm[],
  dictionaries?: IDictionary
) {
  applyPrefills(findFormControlPrefills(forms), dictionaries);
}

/**
 * Calculates only the display-only FORM_CONTROL fields (e.g. an info area) once, for an
 * existing record: their value is never saved, while the saved value of any other
 * prefilled field must stay as it is.
 */
export function applyDisplayOnlyPrefill(
  forms: DynamicForm[],
  dictionaries?: IDictionary
) {
  applyPrefills(
    findFormControlPrefills(forms).filter(({ field }) =>
      isDisplayOnlyControl(field.control)
    ),
    dictionaries
  );
}

function applyPrefills(
  prefills: IFormControlPrefill[],
  dictionaries?: IDictionary
) {
  prefills
    // an empty source leaves the defaultValue of a new form as it is
    .filter(({ source }) => toList(source.value).length)
    .forEach(({ field, source, target }) =>
      updateTarget(field, source, target, dictionaries)
    );
}

interface IFormControlPrefill {
  field: IFormField;
  source: DynamicFormControl;
  target: DynamicFormControl;
}

function findFormControlPrefills(forms: DynamicForm[]): IFormControlPrefill[] {
  return forms.flatMap(form =>
    form.formFields
      .filter(field => field.prefill?.type === PrefillType.FORM_CONTROL)
      .map(field => {
        const sourceName = field.prefill!.field;
        const source = forms
          .find(item => item.hasField(sourceName))
          ?.getControl(sourceName);
        const target = form.getControl(field.name);
        return source && target ? { field, source, target } : null;
      })
      .filter((item): item is IFormControlPrefill => !!item)
  );
}

function updateTarget(
  field: IFormField,
  source: DynamicFormControl,
  target: DynamicFormControl,
  dictionaries?: IDictionary
) {
  const { sourceName } = source.config;
  const options = sourceName
    ? dictionaries?.[resolveDictionaryKey(sourceName)]
    : undefined;
  const result = resolveFormControlPrefill(field, source.value, options);
  if (result && result.value !== target.value) {
    // a display-only control does not make the form touched
    target.setValue(result.value, !isDisplayOnlyControl(field.control));
  }
}

function normalize(value: any) {
  return value?.toString().toLowerCase();
}
